import { Link } from '@tanstack/react-router';
import { Undo2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import type { JobDetail, SelectedRow, SelectedView } from '@nest-recruit/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Avatar } from '../../../components/Avatar';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Chip } from '../../../components/Chip';
import { DataTable } from '../../../components/data-table/DataTable';
import type { Column, SortSpec } from '../../../components/data-table/types';
import { SkeletonLines } from '../../../components/Skeleton';
import { EmptyState, ServerErrorState } from '../../../components/system-states/SystemState';
import { errorMessage } from '../../../lib/api-client';
import { EMPTY, formatDateTime, formatRelative } from '../../../lib/format';
import { EMPTY_SELECTION, type SelectionState } from '../../../lib/selection';
import { useToasts } from '../../../stores/toasts';
import { selectedKey, selectionApi, useSelected } from '../api/comparison-api';
import styles from './SelectedStage.module.css';

// R8 — JOB-12's ✓ Selected (F24.11, §L33): who was chosen, when, by whom and from which round.
// While Offers is off nobody gets an offer draft here; the stage simply holds the Selected.
// A selection can be taken back for ten minutes (F12.10's undo).

export function SelectedStage({ detail }: { readonly detail: Pick<JobDetail, 'id' | 'interviewRounds'> }): ReactNode {
  const jobId = detail.id;
  const selected = useSelected(jobId);
  const queryClient = useQueryClient();
  const show = useToasts((toasts) => toasts.show);
  const [sort, setSort] = useState<readonly SortSpec[]>([]);
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  const [undoing, setUndoing] = useState<ReadonlySet<string>>(new Set());

  if (selected.isPending) return <SkeletonLines count={5} />;
  if (selected.error) return <ServerErrorState onRetry={() => void selected.refetch()} />;
  const view = selected.data;

  async function undo(row: SelectedRow): Promise<void> {
    const key = selectedKey(jobId);
    const before = queryClient.getQueryData<SelectedView>(key);
    // §5a rule 1: off the list at once; back, with the reason, if the server says no.
    queryClient.setQueryData<SelectedView>(key, (current) => (current ? { ...current, rows: current.rows.filter((item) => item.applicationId !== row.applicationId) } : current));
    setUndoing((current) => new Set(current).add(row.applicationId));
    try {
      const next = await selectionApi.undo(jobId, row.applicationId);
      queryClient.setQueryData(key, next);
      await queryClient.invalidateQueries({ queryKey: ['jobs'] });
      show({ tone: 'success', message: `${row.candidate.fullName} is no longer selected`, detail: 'They are back where they were before.' });
    } catch (error) {
      if (before) queryClient.setQueryData(key, before);
      show({ tone: 'danger', message: 'The selection stays', detail: errorMessage(error) });
    } finally {
      setUndoing((current) => {
        const next = new Set(current);
        next.delete(row.applicationId);
        return next;
      });
    }
  }

  const columns: readonly Column<SelectedRow>[] = [
    {
      id: 'candidate',
      header: 'Candidate',
      isIdentity: true,
      defaultWidth: 220,
      exportValue: (row) => row.candidate.fullName,
      cell: (row) => (
        <span className={styles.who}>
          <Avatar name={row.candidate.fullName} />
          <Link to="/applications/$applicationId" params={{ applicationId: row.applicationId }} className={styles.name}>
            {row.candidate.fullName}
          </Link>
        </span>
      ),
    },
    {
      id: 'when',
      header: 'Selected',
      defaultWidth: 190,
      exportValue: (row) => row.selectedAt,
      cell: (row) => (
        <span className={styles.stack}>
          <span>{formatRelative(row.selectedAt)}</span>
          <span className={styles.meta}>{formatDateTime(row.selectedAt)}</span>
        </span>
      ),
    },
    { id: 'by', header: 'By', defaultWidth: 160, exportValue: (row) => row.selectedByName, cell: (row) => row.selectedByName ?? EMPTY },
    { id: 'from', header: 'From', defaultWidth: 170, exportValue: (row) => row.fromRoundName, cell: (row) => row.fromRoundName ?? EMPTY },
    {
      id: 'stage',
      header: 'Now',
      defaultWidth: 150,
      exportValue: (row) => row.stageLabel,
      cell: (row) => (
        <Chip tone="done">
          <span className={styles.dot} aria-hidden />
          {row.stageLabel}
        </Chip>
      ),
    },
    // ADR 0083 (owner 2026-11-01 Q8 a): HR reaches each hire's 30 and 90-day plans from here; joining waits for them.
    {
      id: 'plans',
      header: '30 and 90-day plans',
      defaultWidth: 170,
      cell: (row) => (
        <Link to="/hires/$applicationId/plans" params={{ applicationId: row.applicationId }} className={styles.name}>
          Open the plans
        </Link>
      ),
    },
    ...(view.isOffersOn ? [{ id: 'offer', header: 'Offer', defaultWidth: 140, exportValue: (row: SelectedRow) => row.offerReference, cell: (row: SelectedRow) => row.offerReference ?? EMPTY }] : []),
    {
      id: 'undo',
      header: '',
      defaultWidth: 110,
      cell: (row) =>
        row.canUndo ? (
          <Button variant="ghost" size="sm" icon={Undo2} isPending={undoing.has(row.applicationId)} onClick={() => void undo(row)}>
            Undo
          </Button>
        ) : null,
    },
  ];

  const places = view.places.total === 1 ? '1 place' : `${view.places.total} places`;
  return (
    <div className={styles.stage}>
      <Card
        title="Selected"
        meta={`${view.rows.length} selected · ${places} on this job${view.places.left > 0 ? ` · ${view.places.left} still free` : ' · every place taken'}`}
      >
        <p className={styles.note}>
          {view.isOffersOn
            ? 'Each person selected has an offer draft, ready for the offer details.'
            : 'The recruiter, the requester and the line manager were told by email.'}
        </p>
      </Card>
      <DataTable
        screenId="selected"
        label="Selected candidates"
        columns={columns}
        rows={view.rows}
        getRowId={(row) => row.applicationId}
        total={view.rows.length}
        sort={sort}
        onSortChange={setSort}
        selection={selection}
        onSelectionChange={setSelection}
        isSelectable={false}
        toneOf={() => 'done'}
        emptyState={
          detail.interviewRounds.length === 0 ? (
            <EmptyState
              title="Nobody selected yet"
              body="Once someone passes the test, select them from the test results."
              action={
                <Link to="/jobs/$jobId/$tab" params={{ jobId, tab: 'assessment' }} className={styles.action}>
                  Open the test results
                </Link>
              }
              isInset
            />
          ) : (
            <EmptyState
              title="Nobody selected yet"
              body="Once someone passes the last interview round, select them from the round's result."
              action={
                <Link to="/jobs/$jobId/$tab" params={{ jobId, tab: 'decisions' }} className={styles.action}>
                  Open the decisions
                </Link>
              }
              isInset
            />
          )
        }
      />
    </div>
  );
}
